import type { TranslationKeys } from './lt';

const en: TranslationKeys = {
  cmd: {
    placeholder: 'Search pages, actions...',
    noResults: 'Nothing found',
    navigate: 'move',
    select: 'select',
    close: 'close',
  },

  // App
  app: {
    name: 'A5Suite',
    loading: 'Loading...',
  },

  // Auth
  auth: {
    username: 'Username',
    password: 'Password',
    signIn: 'Sign in',
    signingIn: 'Signing in...',
    loginFailed: 'Login failed',
    signOut: 'Sign out',
  },

  // Navigation
  nav: {
    dashboard: 'Dashboard',
    darbai: "Work",
    invoices: "Invoices",
    portfolio: 'All companies',
    documents: 'Documents',
    users: 'Users',
    closeWizard: 'Close',
    automation: 'Automation',
    analytics: 'Analytics',
    sales: 'Sales',
    purchases: 'Purchases',
    bankingCash: 'Banking & Cash',
    journal: 'Journal',
    assets: 'Assets',
    reports: 'Reports',
    taxCompliance: 'Tax & Compliance',
    close: 'Close',
    setup: 'Setup',
    accounts: 'Accounts',
    contacts: 'Contacts',
    catalog: 'Catalog',
    aiAudit: 'AI & Audit',
    arena: 'Agent Arena',
    settings: 'Settings',
    inventory: 'Inventory',
    payroll: 'Payroll',
    advanceReports: 'Advance Reports',
    adminOnly: 'Admin only',
  },

  // Common
  common: {
    save: 'Save',
    saving: 'Saving...',
    create: 'Create',
    cancel: 'Cancel',
    edit: 'Edit',
    delete: 'Delete',
    close: 'Close',
    back: 'Back',
    toStart: 'To start',
    search: 'Search',
    searchPlaceholder: 'Search by content, doc #...',
    from: 'From',
    to: 'To',
    date: 'Date',
    name: 'Name',
    code: 'Code',
    type: 'Type',
    status: 'Status',
    actions: 'Actions',
    loading: 'Loading...',
    noData: 'No data',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    active: 'Active',
    obsolete: 'Obsolete',
    manage: 'Manage',
    viewAll: 'View all',
    details: 'Details',
    dateFrom: 'Valid From',
    dateTo: 'Valid To',
    person: 'Person',
    content: 'Content',
    amount: 'Amount',
    currency: 'Currency',
    total: 'Total',
    description: 'Description',
    none: '-- None --',
    noPerson: 'No person',
    account: 'Account',
    accountName: 'Account Name',
    docNumber: 'Doc #',
    docType: 'Document Type',
    tryAgain: 'Try Again',
    done: 'Done',
    preview: 'Preview Import',
    required: '* Required',
    language: 'Language',
    unknownError: 'Unknown error',  },

  // Layout
  layout: {
    workspaceSwitchFailed: 'Failed to switch company',
    noWorkspace: 'No Workspace Active',
    createOrImport: 'Create or import a workspace to get started with your accounting data.',
    manageWorkspaces: 'Manage Workspaces',
    workspaces: 'Workspaces',
    company: 'Company',
    area: 'Area',
    searchCtrlK: 'Search (Ctrl+K)',
    moduleLoading: 'Loading module...',
  },

  // Workspace
  workspace: {
    title: 'Workspaces',
    createNew: 'Create New',
    noWorkspaces: 'No workspaces yet — they appear automatically after signing in via integravizija.lt',
  },

  // Dashboard
  dashboard: {
    code: 'Code',
    vat: 'VAT',
    invoice: '+ Invoice',
    journalBtn: 'Journal',
    closeReadiness: 'Close Readiness',
    cashPosition: 'Cash Position',
    anomaliesRisk: 'Anomalies & Risk',
    banking: 'Banking',
    critical: 'critical',
    warnings: 'warnings',
    info: 'info',
    allClear: 'All clear',
    noAnomalies: 'No anomalies detected',
    totalDecisions: 'total decisions logged',
    recentJournal: 'Recent Journal Entries',
    recentInvoices: 'Recent Invoices',
    trialBalance: 'Trial Balance',
    asOfToday: 'As of today',
    vatSummary: 'VAT Summary',
    taxReports: 'Tax reports',
    saftExport: 'SAFT Export',
    xmlForVmi: 'XML for VMI',
    settingsBtn: 'Settings',
    companyAndAccounts: 'Company & accounts',
    accounts: 'Accounts',
    trialBalanceItems: 'Trial Balance Items',
    invoicesMade: 'Invoices (Made)',
    debit: 'Debit',
    credit: 'Credit',
    class: 'Class',
    longTermAssets: 'Long-term assets',
    shortTermAssets: 'Short-term assets',
    equity: 'Equity',
    liabilities: 'Liabilities',
    sales: 'Sales',
    costs: 'Costs',
    noEntries: 'No entries',
    noInvoices: 'No invoices',
    ar: 'AR',
    ap: 'AP',
    out: 'OUT',
    in: 'IN',
    decisionLog: 'Decision Log',
  },

  // Invoices
  invoices: {
    salesTitle: 'Sales Invoices (Made)',
    purchaseTitle: 'Purchase Invoices (Received)',
    newInvoice: '+ New Invoice',
    salesTab: 'Sales (Made)',
    scanTab: 'Scan',
    purchasesTab: 'Purchases (Received)',
    draft: 'Draft',
    post: 'Post',
    client: 'Client',
    supplier: 'Supplier',
    sum: 'Sum',
    vat: 'VAT',
    deleteTitle: 'Delete Invoice',
    deleteMessage: 'Are you sure you want to delete invoice #{{id}}? This action cannot be undone.',
    postTitle: 'Post Invoice',
    postMessage: 'Post invoice #{{id}} to the general ledger? This will create a journal entry.',
    noInvoicesFound: 'No invoices found',
    // Credit Note
    creditNoteTitle: 'New Credit Note',
    createCreditNote: 'Credit Note',
    creatingCreditNote: 'Creating credit note...',
    linkedInvoice: 'Linked Invoice',
    unlinkInvoice: 'Unlink',
    searchInvoicePlaceholder: 'Search invoice by number, person...',
    creditNoteFor: 'Credit note for',
    creditNoteContentPlaceholder: 'Credit note description...',
    invoiceDate: 'Invoice Date',
    documentNumber: 'Document #',
    currencyRate: 'Currency Rate',
    unit: 'Unit',
    price: 'Price',
    qty: 'Qty',
    vatRate: 'VAT Rate',
    lineItems: 'Line Items',
    addLine: 'Add Line',
    none: 'None',
    totalWithVat: 'Total with VAT',
    selectPerson: '-- Select --',
  },

  // Journal
  journal: {
    title: 'Journal Entries',
    newEntry: '+ New Entry',
    allEntries: 'All Entries',
    postingLog: 'Posting Log',
    templates: 'Templates',
    source: 'Source',
    allSources: 'All Sources',
    manualEntry: 'Manual Entry',
    invoicePosting: 'Invoice Posting',
    bankOperation: 'Bank Operation',
    periodClosing: 'Period Closing',
    aiGenerated: 'Auto-generated',
    adjustment: 'Adjustment',
    debit: 'Debit',
    credit: 'Credit',
    balanced: 'Balanced',
    notBalanced: 'Not balanced',
    unbalanced: 'UNBALANCED',
    totalDebit: 'Total Debit',
    totalCredit: 'Total Credit',
    difference: 'Difference',
    noJournalEntriesFound: 'No journal entries found',
    noPostingLogEntries: 'No posting log entries',
    posted: 'Posted',
    postedDate: 'Posted Date',
    auditTrailDescription: 'Audit trail of all journal postings showing who, what, when, and source. Every financial posting is immutable once created.',
    createTemplate: '+ Create Template',
    useTemplate: 'Use Template',
    // Journal Edit
    editEntry: 'Edit Journal Entry #{{id}}',
    newJournalEntry: 'New Journal Entry',
    documentNumber: 'Document #',
    documentType: 'Document Type',
    content: 'Content',
    person: 'Person',
    contentPlaceholder: 'Description of the journal entry...',
    docRequired: 'Document number is required',
    contentRequired: 'Content is required',
    lineWithAccountRequired: 'At least one line with an account and positive amount is required',
    everyLineMustHaveAccount: 'Every line with an amount must have an account selected',
    entryNotBalanced: 'Entry is not balanced. Debit: {{debit}}, Credit: {{credit}}, Difference: {{difference}}',
    futureDateWarning: 'Warning: the date is in the future.',
    noDebitLines: 'No debit lines',
    noCreditLines: 'No credit lines',
    debitTotal: 'Debit (Debetas)',
    creditTotal: 'Credit (Kreditas)',
    addDebit: '+ Debit',
    addCredit: '+ Credit',
    saveChanges: 'Save Changes',
    createEntry: 'Create Entry',
    // Journal Detail
    entryNotFound: 'Entry not found',
    entryDeleted: 'Journal entry #{{id}} deleted',
    deleteTitle: 'Delete Journal Entry',
    deleteMessage: 'Permanently delete journal entry #{{id}}? This cannot be undone.',
    cloneTitle: 'Clone Journal Entry',
    cloneMessage: 'Create a copy of journal entry #{{id}} with today\'s date?',
    clone: 'Clone',
    lines: 'Lines',
    // Document types
    docTypeGeneric: 'Generic',
    docTypeSalesInvoice: 'Sales Invoice',
    docTypePurchaseInvoice: 'Purchase Invoice',
    docTypeBankOp: 'Bank Operation',
    docTypeCashIncome: 'Cash Income',
    docTypeCashSpending: 'Cash Spending',
    docTypeOffset: 'Offset',
    docTypeWageSheet: 'Wage Sheet',
    // Document type labels keyed by the select value (JournalEdit)
    docType: {
      sp: 'Generic',
      sf: 'Sales Invoice',
      sg: 'Purchase Invoice',
      b: 'Bank Operation',
      kio: 'Cash Income',
      kpo: 'Cash Spending',
      sk: 'Offset',
      bd: 'Wage Sheet',
    },
    docNumberPlaceholder: 'e.g. T-001',
    personOptional: 'Person (optional)',
    removeLine: 'Remove line',
  },

  // Settings
  settings: {
    title: 'Settings',
    companyInfo: 'Company Information',
    companyName: 'Name',
    companyCode: 'Company Code',
    vatCode: 'VAT Code',
    address: 'Address',
    headPerson: 'Head Person',
    createCompany: 'Create Company',
    createCompanyDescription: 'No company configured yet. Fill in the details below to get started.',
    taxRates: 'Tax Rates',
    addTaxRate: '+ Add Tax Rate',
    rate: 'Rate (%)',
    vatDebitAccount: 'VAT Debit Account',
    vatCreditAccount: 'VAT Credit Account',
    vatPurchaseAccount: 'VAT Purchase Account',
    validFrom: 'Valid From',
    noTaxRates: 'No tax rates',
    failedToCreateTaxRate: 'Failed to create tax rate',
    documentSerials: 'Document Serials',
    manageSerials: 'Manage Serials',
    lastNumber: 'Last #',
    pattern: 'Pattern',
    reset: 'Reset',
    noDocumentSerials: 'No document serials configured',
    aiAgentSettings: 'AI Agent Settings',
    aiAgentDescription: 'Configure authority levels per AI workflow',
    workflow: 'Workflow',
    authorityLevel: 'Authority Level',
    aiDescription: 'Description',
    noAiWorkflows: 'No AI workflows configured',
    authorityAuditNote: 'Authority changes are audit-logged. Default is "Suggest" until explicitly promoted.',
    saveChanges: 'Save Changes ({{count}})',
    administration: 'Administration',
    docSerialsDescription: 'Manage document numbering patterns, prefixes, and sequences',
    userRolesPermissions: 'User Roles & Permissions',
    rolesDescription: 'Manage roles and module-level access control',
    dataBackup: 'Data Backup',
    backupDescription: 'Export company data as JSON backup file',
    emailConfiguration: 'Email Configuration',
    emailDescription: 'SMTP settings for invoice email sending',
  },

  // Accounts
  accounts: {
    title: 'Chart of Accounts',
    trialBalance: 'Trial Balance',
    newAccount: '+ New Account',
    accountNumber: 'Account Number',
    saftCode: 'SAFT Code',
    number: 'Number',
    noAccounts: 'No accounts found',
    deleteConfirm: 'Delete account {{number}}?',
    classLabel: 'Class',
  },

  // Persons
  persons: {
    title: 'Contacts',
    subtitle: 'Unified customers, vendors, and employees',
    addContact: 'Add Contact',
    total: 'Total',
    customers: 'Customers',
    vendors: 'Vendors',
    employees: 'Employees',
    custVend: 'Cust+Vend',
    searchPlaceholder: 'Search by name, code, or VAT code...',
    allContacts: 'All Contacts',
    customerOnly: 'Customers Only',
    vendorOnly: 'Vendors Only',
    employeeOnly: 'Employees Only',
    custPlusVend: 'Customer + Vendor',
    hasAnyRole: 'Has any role',
    receivables: 'Receivables',
    payables: 'Payables',
    roles: 'Roles',
    noRole: 'No role',
    both: 'Both',
    noContacts: 'No contacts found',
    editContact: 'Edit Contact',
    newContact: 'New Contact',
    receivablesAccount: 'Receivables Account',
    payablesAccount: 'Payables Account',
    customer: 'Customer',
    vendor: 'Vendor',
    employee: 'Employee',
    naturalPerson: 'Natural Person',
    update: 'Update',
    personDeleted: 'Person #{{id}} deleted',
    deleteConfirm: 'Delete {{name}}?',
    inactive: 'Inactive',
    // Address fields
    street: 'Street',
    city: 'City',
    postalCode: 'Postal Code',
    country: 'Country',
    // Contact fields
    contactName: 'Contact Person',
    contactPhone: 'Phone',
    contactEmail: 'Email',
    // Business terms
    paymentTermsDays: 'Payment Terms (days)',
    discountPercent: 'Discount (%)',
    creditLimit: 'Credit Limit',
    sector: 'Business Sector',
    notes: 'Notes',
  },

  // Periods
  periods: {
    closeTitle: 'Period Close',
  },

  // Reports
  reports: {
    nav: {
      title: 'Reports',
      vat: 'VAT summary',
      declaration: 'VAT declaration',
      financial: 'Financial statements',
      debtAging: 'Debt aging',
      close: 'Close',
      saft: 'Export (SAFT)',
    },
    vatSummary: 'VAT Summary',
    vatDeclaration: 'VAT Declaration',
    financialStatements: 'Financial Statements',
    debtAging: 'Debt Aging',
    exportSaft: 'SAFT Export',
  },

  // Banking
  banking: {
    title: 'Banking & Cash',
    import: 'Bank Statement Import',
    importStatement: 'Import Statement',
    newOperation: '+ New Operation',
    cashOperations: 'Cash Operations',
    offsets: 'Offsets',
    inflowCredit: 'Inflow (Credit)',
    outflowDebit: 'Outflow (Debit)',
    netCashMovement: 'Net Cash Movement',
    debitAcct: 'Debit Acct',
    creditAcct: 'Credit Acct',
    noBankOperations: 'No bank operations found for this period',
    editBankOperation: 'Edit Bank Operation',
    newBankOperation: 'New Bank Operation',
    documentNumberLabel: 'Document Number *',
    amountLabel: 'Amount *',
    debitAccountLabel: 'Debit Account',
    creditAccountLabel: 'Credit Account',
    bankAccountCodeLabel: 'Bank Account Code',
    personLabel: 'Person',
    currencyLabel: 'Currency',
    currencyRateLabel: 'Currency Rate',
    updateOperation: 'Update',
    createOperation: 'Create',
    deleteOperation: 'Delete operation',
    operationDeleted: 'Bank operation #{{id}} deleted',
    reconciliationWorkspace: 'Reconciliation Workspace',
    reconciliationDescription: 'Bank reconciliation will match bank feed transactions with internal records. This feature requires bank feed integration to be configured in Settings.',
    startReconciliation: 'Start Reconciliation (coming soon)',
    selectAccount: '-- Select --',
    selectPerson: '-- None --',
    docNumberPlaceholder: 'e.g. BO-001',
    // Bank Import
    smartBankImport: 'Smart Bank Import',
    backToBanking: 'Back to Banking',
    total: 'Total',
    highConfidence: 'High Confidence',
    partialMatch: 'Partial Match',
    noMatch: 'No Match',
    accepted: 'Accepted',
    totalInflow: 'Total Inflow',
    totalOutflow: 'Total Outflow',
    detectedFormat: 'Detected Format',
    acceptAllGreen: 'Accept all green ({{count}})',
    acceptAll: 'Accept all',
    confirmOperations: 'Confirm {{count}} operations',
    creating: 'Creating...',
    parseWarnings: 'Parse Warnings',
    counterparty: 'Counterparty',
    confidence: 'Confidence',
    importComplete: 'Import Complete',
    created: 'Created',
    skipped: 'Skipped',
    errors: 'Errors',
    importAnotherFile: 'Import Another File',
    viewBankingOperations: 'View Banking Operations',
    accountNumberHint: 'Account number for your bank account in the chart of accounts',
    dragAndDrop: 'Drag & drop a bank statement file here',
    supportedFormats: 'Supported formats: CSV (SEB, Swedbank, Luminor), XML (Camt.053), MT940 (SWIFT)',
    browseFiles: 'Browse Files',
    pasteCsvText: 'Paste CSV Text',
    pasteBankStatement: 'Paste Bank Statement',
    backToFileUpload: 'Back to file upload',
    bankFormat: 'Bank Format',
    autoDetect: 'Auto-detect',
    parseAndMatch: 'Parse & Match',
    parsing: 'Parsing...',
    noOperations: 'No operations parsed',
    // Bank Accounts
    bankAccounts: 'Bank Accounts',
    manageAccounts: 'Manage Accounts',
    newAccount: '+ New Account',
    accountDeleted: 'Bank account deleted',
    accountUpdated: 'Bank account updated',
    accountCreated: 'Bank account created',
    deleteAccountConfirm: 'Delete this bank account?',
    accounts: 'Accounts',
    balances: 'Balances',
    statement: 'Statement',
    accountName: 'Account Name',
    bankNameCol: 'Bank',
    glAccount: 'GL Account',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    type: 'Type',
    noBankAccounts: 'No bank accounts configured',
    totalNet: 'Net Balance',
    debit: 'Debit',
    credit: 'Credit',
    netBalance: 'Net Balance',
    noBalances: 'No balances available',
    openingBalance: 'Opening',
    periodDebit: 'Period Debit',
    periodCredit: 'Period Credit',
    periodNet: 'Period Net',
    closingBalance: 'Closing',
    noTransactions: 'No transactions in this period',
    selectAccountForStatement: 'Select a bank account to view its statement',
    chooseAccount: '-- Choose Account --',
    allTypes: 'All',
    typeBank: 'Bank Account',
    typePseudo: 'Pseudo Bank',
    typeTill: 'Cash Register',
    accountNameLabel: 'Account Name',
    bankNameLabel: 'Bank Name',
    bankCodeLabel: 'Bank Code',
    typeLabel: 'Account Type',
    glAccountLabel: 'GL Account Number',
    editAccount: 'Edit Bank Account',
    // Reconciliation
    showUnreconciled: 'Show Unreconciled',
    hideUnreconciled: 'Hide Unreconciled',
    unreconciledCount: 'unreconciled transaction(s)',
    markReconciled: 'Mark Reconciled',
    allReconciled: 'All transactions are reconciled!',
    reconciliationUpdated: 'Reconciliation status updated',
  },

  // AI Audit
  aiAudit: {
    title: 'AI & Audit',
  },

  // Agent Arena
  arena: {
    title: 'Agent Arena',
    description: 'AI agent testing and competition environment.',
    comingSoon: 'Arena feature is being prepared. Stay tuned for updates.',
  },

  // Audit Trail
  audit: {
    title: 'Audit Trail',
  },

  // Catalog
  catalog: {
    title: 'Goods & Services Catalog',
  },

  // Error Boundary
  errorBoundary: {
    title: 'Something went wrong',
    message: 'An error occurred in the application. Try reloading the page.',
    reload: 'Reload',
  },

  // Confirm Dialog
  confirmDialog: {
    cancel: 'Cancel',
  },

  // Modules
  modules: {
    advanceReports: 'Advance Reports',
    fixedAssets: 'Fixed Assets',
    inventory: 'Inventory',
    payroll: 'Payroll',
  },
  // Analytics drill-down dashboard
  users: {
    title: 'Users',
    subtitle: 'Roles of integravizija.lt sign-ins — without a role change nobody can work here (Viewer is read-only)',
    bridged: 'via integravizija.lt',
    local: 'local',
    active: 'Active',
    inactive: 'Inactive',
    empty: 'No users.',
    roleChangeFailed: 'Failed to change the role.',
    col: {
      user: 'User',
      role: 'Role',
      status: 'Status',
      lastLogin: 'Last sign-in',
    },
    role: {
      admin: 'Administrator',
      senior_accountant: 'Senior accountant',
      accountant: 'Accountant',
      viewer: 'Viewer',
    },
    hint: 'Accountant can create and edit documents; Viewer is read-only. You cannot change your own role — ask another admin.',
  },
  routines: {
    title: 'Automation',
    subtitle: 'You author the routines — the system premakes the work (drafts); you confirm',
    newRoutine: 'New routine',
    newTitle: 'New routine',
    editTitle: 'Edit routine',
    active: 'Active routines',
    empty: 'No routines yet — create the first one or use the suggestions below.',
    statusActive: 'Active',
    statusPaused: 'Paused',
    kind: {
    Reminder: 'Reminder',
    reminderTypeLabel: 'What to remind',
    reminderType: {
      BankImport: 'Bank statement import',
      PeriodClose: 'Period close',
    },
    graceDaysLabel: 'Grace days',
    graceDaysHint: 'Days after the period start/end before the nudge fires.',
      PayrollAccrual: 'Payroll / VSD accrual',
      RecurringInvoice: 'Recurring invoice',
    },
    kindLabel: 'Kind',
    nameLabel: 'Name',
    cadenceLabel: 'Cadence',
    cadence: {
      Monthly: 'monthly',
      Weekly: 'weekly',
      Annually: 'annually',
    },
    weekdayLabel: 'Day of week',
    weekday: {
      0: 'Monday',
      1: 'Tuesday',
      2: 'Wednesday',
      3: 'Thursday',
      4: 'Friday',
      5: 'Saturday',
      6: 'Sunday',
    },
    triggerDay: 'Trigger day',
    phaseLabel: 'Period',
    phase: {
      lastMonth: "last month's work (at month end)",
      currentMonth: "this month's work (on the trigger day)",
    },
    overdueLabel: 'Warn after (days)',
    templateLabel: 'Template (JSON)',
    templateHint: 'Lines with accounts and amount modes: Fixed / FromHistoryLast / FromHistoryAverage. Suggestions fill this in.',
    save: 'Save routine',
    saveFailed: 'Failed to save the routine.',
    pause: 'Pause',
    resume: 'Resume',
    deleteConfirm: 'Delete routine "{{name}}"? Its work will no longer be premade.',
    draftPosted: 'posted',
    draftPending: 'awaiting confirmation',
    neverRan: 'has not fired yet',
    runNow: 'Run now',
    runSummary: 'Checked {{checked}} routine(s): {{premade}} draft(s) premade, {{alarms}} alarm(s).',
    runFailed: 'Check failed',
    suggestions: 'Suggestions from your books',
    suggestionsHint: 'The system notices recurring work and prefills the form — you always author the final routine.',
    noSuggestions: 'No recurring work detected yet.',
    prefill: 'Prefill form',
  },
  closeWizard: {
    title: 'Close wizard',
    subtitle: 'Step by step — all the way to the real closing entry',
    closeDate: 'Close date',
    annual: 'Annual close',
    lastClosed: 'Last closed',
    step: { overview: 'Overview', cleanup: 'Clean-up', dryrun: 'Dry run', close: 'Close', achievement: 'Achievement' },
    investigate: 'Ask',
    investigateTemplate: "Closing period {{period}}. Before the close this issue remains: {{item}}. Explain where it comes from and how to resolve it. Answer briefly (up to 5 sentences) and name one concrete next step.",
    unposted: 'Unposted entries: {{count}} — all must be posted before closing',
    openJournal: 'Open journal',
    alreadyClosed: 'Period {{date}} is already closed',
    alreadyClosedHint: 'To close a later period, pick a later date.',
    dateInvalid: 'Close date must be after the last closing ({{last}}).',
    checklistErrorsCount: 'Pre-close check found {{count}} error(s) — see below.',
    classesZero: 'All class 5/6 accounts are zero',
    andMore: '+ {{count}} more',
    continueBlocked: 'Resolve the red items above first',
    checklistOnOverview: '{{errors}} error(s) and {{warnings}} warning(s) — shown on the overview step.',
    trialBalance: 'Trial balance',
    balanced: 'Balanced',
    imbalance: 'Imbalance: {{amount}}',
    pAndLClasses: 'Class 5/6 balances',
    continue: 'Continue',
    back: 'Back',
    forClose: 'Must be resolved before closing {{date}}',
    noChecklistIssues: 'No issues in the close checklist',
    currentState: 'Current state (not part of this close)',
    currentStateHint: 'These items are about the books generally, not required for this close — but a good moment to handle them.',
    toDryRun: 'Dry run',
    dryRunTitle: 'Dry run (nothing is written)',
    dryRunRunning: 'Computing…',
    dryRunPassed: 'Dry run passed — {{lines}} closing lines would be created',
    dryRunFailed: 'Dry run failed',
    closingDebits: 'Closing debits',
    netResult: 'Projected result',
    agmSkipped: 'No AGM date given — profit will not be transferred to retained earnings (can be re-run later with a date).',
    toClose: 'Go to close',
    closeTitle: 'Close the period through {{date}}',
    agmDate: 'AGM approval date (optional)',
    agmHint: 'When provided, profit is transferred to retained earnings per VAS 4.',
    manualClose: 'Manual close with explicit accounts',
    closeNow: 'Close now',
    confirmTitle: 'Confirm the close',
    confirmMessage: 'Close the period through {{date}}? Entries in this period will be locked after closing.',
    closeFailed: 'Close failed',
    forbidden: 'Closing requires administrator or senior accountant rights.',
    achievement: '{{date}} closed',
    missingAccounts: 'Closing accounts are not configured — the close cannot start without them',
    proposedAccounts: 'Proposed from the {{from}} close: balance summary {{summary}}, current profit {{current}}, retained profit {{former}}. Review and set.',
    noProposal: 'No closing entry found in history to propose accounts from — enter them in Settings (company accounts).',
    setAccounts: 'Set the proposal',
    accountsSet: 'Closing accounts configured',
    unpostedAfter: '{{count}} unposted entries dated after the close date (test data?) — they do not block the close but pollute the book',
    whatsNext: 'What happens next',
    nextStatements: 'The annual financial statement is filed with the Centre of Registers.',
    nextDeclaration: 'The annual tax declaration is filed with the tax authority.',
    nextOutside: 'Filing these documents is not yet part of this system — your books are closed correctly; file through your usual channels.',
    achievementHint: 'The closing entry is created, the result carried forward, the balance verified. If something must be fixed, the period can be reopened (carefully, if reports were already filed).',
    income: 'Income',
    expenses: 'Expenses',
    netProfit: 'Result',
  },
  documents: {
    title: 'Documents',
    subtitle: 'Supplier invoice upload, recognition and review',
    dropTitle: 'Drop or choose a supplier invoice (PDF, JPG, PNG up to 10 MB)',
    dropHint: 'Recognition is automatic — review everything before confirming.',
    uploading: 'Uploading and recognizing…',
    uploadFailed: 'Failed to upload or recognize the document.',
    listTitle: 'Documents',
    empty: 'No documents yet.',
    selectHint: 'Choose a document from the list.',
    openFile: 'Open file',
    recognizing: 'Recognizing… (up to a few minutes)',
    retry: 'Retry recognition',
    retrying: 'Retrying…',
    reject: 'Reject',
    rejectConfirm: 'Reject this scan?',
    rejectedInfo: 'Scan rejected. The file is retained for the retention period.',
    confirm: 'Confirm → draft purchase invoice',
    confirming: 'Creating draft…',
    confirmFailed: 'Failed to create the draft.',
    askClient: 'Ask the client about this document',
    askPlaceholder: 'What to ask? (the question appears in the Waiting on client tab)',
    askSend: 'Ask',
    asked: 'Question recorded — visible under Attention → Waiting on client.',
    confirmHint: 'Creates an UNPOSTED purchase invoice draft. Posting/export to Apskaita5 is a later version.',
    confirmedTitle: 'Draft purchase invoice created',
    confirmedHint: 'The draft is unposted — review and post it under Purchases.',
    openPurchases: 'Open Purchases',
    crosscheckTitle: 'Arithmetic check failed — values kept as scanned',
    nonEur: 'Invoice is not EUR ({{currency}}) — only EUR is supported for now.',
    needPerson: 'Choose the supplier',
    choosePerson: 'choose supplier',
    serviceLine: 'Service',
    totals: 'Total',
    vat: 'VAT',
    f: {
      supplier: 'Supplier',
      number: 'Invoice no.',
      date: 'Date',
      net: 'Net total',
      total: 'Total',
      person: 'Supplier (company register)',
      content: 'Content',
      defaultAccount: 'Expense account',
      defaultVatAccount: 'VAT account',
    },
    col: {
      name: 'Name',
      unit: 'Unit',
      qty: 'Qty',
      price: 'Price',
      vat: 'VAT',
      sum: 'Sum',
      account: 'Account',
      defaultAccount: '(default)',
    },
                journey: {
          title: 'Fiscal years',
      refreshing: 'Refreshing…',
          current: '{{year}} in progress',
          closedAt: 'Closed {{date}}',
        },
        docNotFound: 'Document {{token}} not found.',
      commentLabel: 'Comment (optional)',
      dayEarlier: 'One day earlier',
      dayLater: 'One day later',
      case: {
      title: 'Work item',
      stickyBadge: 'Awaiting a decision',
      close: 'Close',
      backToCase: 'Back to the case',
    },
    status: {
      Uploaded: 'Uploaded',
      Recognizing: 'Recognizing',
      Recognized: 'Recognized',
      RecognitionFailed: 'Failed',
      Confirmed: 'Confirmed',
      Rejected: 'Rejected',
    },
    error: {
      llm_unavailable: 'Recognition service unavailable. Check the key and retry.',
      scan_failed: 'Recognition failed. Retry.',
      file_too_large: 'File too large (10 MB limit).',
      unsupported_media: 'Unsupported file type (PDF, JPG, PNG only).',
    },
    check: {
      triangle: 'net + VAT does not match the grand total',
      'net-missing': 'net total missing while line items exist',
      'lines-vs-net': 'line sums do not match the net total',
    },
  },
  analytics: {
    title: 'Work',
    loading: 'Loading…',
    dataAsOf: 'Data as of {{time}}',
    sync: {
      title: 'Syncing data…',
      subtitle: "This workspace's first data sync hasn't finished yet. Your metrics appear right after it.",
      hint: 'Nothing to do — the page refreshes itself',
    },
    back: 'Start over',
    topDebtors: 'Largest debtors',
    tile: {
      receivables: 'Receivables',
      overdue: 'Overdue receivables',
      payables: 'Payables',
      cash: 'Cash',
      vat: 'VAT (month)',
      findings: 'Findings',
      debtors: 'Debtors: {{count}}',
      overdueShare: '{{value}} of receivables overdue',
    },
    inbox: {
      empty: 'All clear',
      observations: 'Observations',
      observationsHint: 'System notices — not work. Click "Not a problem" if you know the cause.',
      emptyHint: 'No deadlines, debts, or unmatched payments need action right now.',
      runCheck: 'Run a check',
      running: 'Checking…',

        rules: {
        title: "Auto-match rules",
        empty: "Exact-match payments are always auto-settled. No additional rules.",
        disable: "Disable",
        timesUsed: "used {{count}}×",
        stopAuto: "Stop automating for this client",
        toastHint: "You can disable anytime via the 🤖 Auto button.",
        },
      overdueBy: '{{days}} d overdue',
    postponedEmpty: 'Nothing postponed.',
    postponedSoon: 'returns in {{days}} d.',
      doneToday: 'Done today: {{count}}',
      tabs: {
        darbai: 'Work',
        waiting: 'Waiting on client',
        postponed: 'Postponed',
        cases: 'Cases',
        auto: 'Done',
      },
      card: {
        what: 'What',
        where: 'Where',
        how: 'How',
        toInvoice: 'Invoice',
        toJournal: 'Entry',
        toPerson: 'Client',
        investigate: 'Ask',
        askClient: 'Ask the client',
        done: 'Done',
        postpone: 'Postpone',
        postponeUntil: 'Postpone until',
        postponeConfirm: 'Postpone',
        notProblem: 'Not a problem',
        finishGroup: 'Finish',
            status: {
        label: 'Status…',
        apply: 'Apply',
      },
        declinePlaceholder: "Why is this not a problem?",
        askPlaceholder: 'What to ask the client about this record?',
        askSend: 'Ask',
        asked: 'Question recorded (Attention → Waiting on client)',
        actionFailed: 'Action failed.',
        confirmAndPost: 'Confirm and post',
        catchUp: 'Recover missed ({{count}})',
        confirmAll: 'Confirm all',
        openWizard: 'Open the close wizard',
        openBankImport: 'Open bank import',
        confirmFailed: 'Failed to post the draft.',
        askTemplate: 'Investigate this work item: {{title}}. {{ids}}. Explain what is wrong and suggest how to resolve it. Answer briefly (up to 5 sentences) and name one concrete next step.',
        askJournal: 'journal entry #{{id}}',
        askInvoice: 'invoice #{{id}}',
        askPerson: 'client #{{id}}',
      },
      atlikta: {
        ManualDone: 'Done',
        Declined: 'Not a problem',
        Postponed: 'Postponed',
        restore: 'Restore',
      },
      tabsEmpty: {
        waiting: 'Nothing is waiting on a client reply yet.',
        waitingHint: 'Client questions, missing documents and approvals will appear here once the client portal is connected.',
        cases: 'No active cases.',
        casesHint: 'Investigations — unclear operations, tax questions, consultations — will collect here once we start registering them.',
        auto: 'The system has not completed anything automatically yet.',
        autoHint: 'Exactly matching payments will be reconciled automatically and shown here with evidence.',
      },
      waiting: {
        newQuestion: 'New question for the client',
        kind: {
          Question: 'Question',
          MissingDocument: 'Missing document',
          Approval: 'Approval',
        },
        asked: 'asked',
        due: 'expected by',
        document: 'Document',
        answerPlaceholder: "Client's answer (record when they reply)…",
        saveAnswer: 'Record answer',
        cancelRequest: 'Cancel',
        textPlaceholder: 'What do you want to ask the client?',
        ask: 'Ask',
      },

      cat: {
        Deadline: 'Deadline',
        OverdueInvoice: 'Debt',
        UnmatchedPayment: 'Payment',
        PayrollAccrual: 'Payroll',
        Finding: 'Finding',
      },
      col: {
        category: 'Category',
        title: 'What',
        amount: 'Amount',
        due: 'Due',
        age: 'Age',
      },
    },
    aging: {
      title: 'Receivables aging',
      titlePayable: 'Payables aging',
      ariaLabel: 'Receivables aging bar',
      current: 'Current',
      range: '{{from}}–{{to}} d',
      over90: '90+ d',
      b1: '1–30 d',
      b31: '31–60 d',
      b61: '61–90 d',
      empty: 'No receivables',
    },
    problems: {
      title: 'Detected problems',
      empty: 'No problems detected',
      lastRun: 'Last check',
    },
    fact: {
      receivablesTotal: 'Receivables total: {{value}}',
      overdueTotal: 'Overdue: {{value}}',
      overdueSharePct: 'Overdue share: {{value}}',
      debtorsCount: 'Debtors: {{value}}',
      top3SharePct: 'Top 3 customers — {{value}} of receivables',
      over90Total: 'Older than 90 days: {{value}}',
      over90SharePct: '90+ d share: {{value}}',
      payablesTotal: 'Payables: {{value}}',
      cashTotal: 'Cash: {{value}}',
      vatNetPayableMonth: 'VAT this month: {{value}}',
    },
    nl: {
      placeholder: 'Ask naturally: "owing more than 1000"…',
      go: 'Investigate',
      thinking: 'Thinking…',
      fallback: 'Could not interpret the query — searching as text',
    },
    explain: {
      title: 'Explanation',
      button: 'Explain',
      loading: 'Explaining…',
    },
    view: {
      inbox: 'Needs attention',
      overview: 'Overview',
      customers: 'Debtors',
      customer: 'Customer',
      invoice: 'Invoice',
      journal: 'Journal entry',
      findings: 'Findings',
      bank: 'Cash',
      vat: 'VAT',
    },
    journal: {
      total: 'Total debit',
      lines: 'Entry lines',
      debit: 'Debit',
      credit: 'Credit',
      noLines: 'No lines',
      entryTile: 'Journal entry',
    },
    customer: {
      debt: 'Receivable',
      turnoverDebit: 'Debit turnover',
      turnoverCredit: 'Credit turnover',
      invoices: 'Invoices',
      invoicesList: 'Customer sales invoices',
      editPerson: 'Edit contact',
      noInvoices: 'No invoices',
    },
    invoice: {
      settleFailed: 'PAYMENT NOT RECONCILED — please try again',

      0: "i",

      1: "n",

      2: "v",

      3: "o",

      4: "i",

      5: "c",

      6: "e",
      settle: "Match",
    teachTitle: "Do this automatically next time?",
    teachBody: "Payments from this client matching invoices (±5%) will be settled without confirmation.",
            settleConfirmTitle: "Match this payment to the invoice?",
      settleConfirmBody: "The payment and invoice will be linked (reconciled). No separate journal entry is created.",
      settleConfirmHint: "The link can be reviewed and removed later.",
      matchesTitle: 'Possible matching payments',
      noMatches: 'No matching payments found',
      delta: 'Difference',
      journalFallback: "Showing as journal entry (no separate invoice record).",
      notFound: "Invoice not found.",
      doc: 'Invoice',
    },
    bank: {
      openFull: 'All bank statements',
    },
    vat: {
      salesVat: 'Sales VAT',
      purchaseVat: 'Purchase VAT',
      net: 'Net VAT payable',
      openFull: 'Open the full VAT report',
    },
    col: {
      total: 'Total',
      amount: 'Amount',
      balance: 'Balance',
      docNo: 'Document no.',
      content: 'Content',
      time: 'Time',
      scanner: 'Scanner',
      severity: 'Severity',
      title: 'Title',
      account: 'Account',
    },
    table: {
      rows: 'rows',
      clearFilters: 'clear filters',
      empty: 'No records',
      loading: 'Loading…',
      next: 'Next',
    },
    ask: {
      link: 'Ask',
      title: 'Ask',
      subtitle: 'What would you like to know about your finances?',
      hint: 'Ask naturally — the answer arrives as views you can dig into.',
      placeholder: 'Ask about receivables, debts, payments…',
      send: 'Send',
      starter1: 'What should I know about receivables?',
      starter2: 'Who is most overdue?',
      starter3: 'Are there unmatched payments?',
      starter4: 'How are we doing this month?',
      statusFetching: 'Gathering data…',
      statusComposing: 'Composing the answer…',
      errorText: 'Could not compose an answer.',
      retry: 'Retry',
      cardBack: 'Back',
      conversation: 'Conversation',
      expand: 'Expand conversation',
      collapse: 'Collapse conversation',

      viewArriving: 'Composing a view…',

      lockedTitle: 'Answers are disabled in this workspace',
      caseBanner: 'Investigating a work item',
      backToCase: 'Back to the work item',
      lockedBody: 'The workspace owner has not allowed sending data to an AI model. The deterministic analytics dashboard works as always.',
      dashboardLink: 'Open the dashboard',
    },
  },
  darbai: {
    title: "Work queue",
    summary: "{{companies}} companies · {{items}} need attention",
    allClear: "All companies clear",
    allClearHint: "No deadlines, debts, or unmatched payments.",
    companyClear: "all clear ✓",
    companyAttention: "{{count}} need attention",
    queueTitle: "Queue across all companies",
  },
};

export default en;
